import { useState } from 'react'

import { ManageAccounts } from '@mui/icons-material'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { isAxiosError } from 'axios'
import { useSnackbar } from 'notistack'

import pspLogo from '~/assets/ISOLOGO-03.svg'

import useImpersonateUser from '../hooks/useImpersonateUser'

interface ImpersonateUserModalProps {
  isOpened: boolean
  setIsOpened: (isOpened: boolean) => void
}

export default function ImpersonateUserModal({
  isOpened,
  setIsOpened,
}: ImpersonateUserModalProps) {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const impersonateMutation = useImpersonateUser()
  const [username, setUsername] = useState('')

  const isLoading = impersonateMutation.isLoading

  function closeModal() {
    if (isLoading) return
    setUsername('')
    setIsOpened(false)
  }

  async function handleImpersonate() {
    const normalizedUsername = username.trim()
    if (!normalizedUsername || isLoading) return

    try {
      await impersonateMutation.mutateAsync(normalizedUsername)
      setUsername('')
      setIsOpened(false)
    } catch (error) {
      const message =
        isAxiosError(error) && error.response?.status === 400
          ? t('impersonate.userNotFound')
          : t('impersonate.error')
      enqueueSnackbar(message, { variant: 'error' })
    }
  }

  return (
    <Modal
      open={isOpened}
      onClose={closeModal}
      aria-labelledby="impersonate-user-title"
      aria-describedby="impersonate-user-description"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Card sx={{ width: 420, mx: 2, outline: 'none' }}>
        <CardHeader
          avatar={<img src={pspLogo} alt="PSP Logo" style={{ height: 32 }} />}
          title={
            <Typography
              id="impersonate-user-title"
              variant="h6"
              fontWeight={600}
            >
              {t('impersonate.title')}
            </Typography>
          }
        />
        <Divider />
        <CardContent>
          <Stack spacing={3}>
            <Typography
              id="impersonate-user-description"
              variant="body2"
              color="text.secondary"
            >
              {t('impersonate.description')}
            </Typography>
            <TextField
              label={t('impersonate.usernameLabel')}
              value={username}
              onChange={event => {
                setUsername(event.target.value)
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') void handleImpersonate()
              }}
              disabled={isLoading}
              autoFocus
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '20px' } }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="text" onClick={closeModal} disabled={isLoading}>
                {t('general.cancel')}
              </Button>
              <Button
                variant="contained"
                startIcon={<ManageAccounts />}
                onClick={() => {
                  void handleImpersonate()
                }}
                disabled={isLoading || !username.trim()}
              >
                {t('impersonate.confirm')}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Modal>
  )
}
